import {
    AssignMobileOfferRequest,
    MobileOfferAssignmentReadinessStatus,
    MobileOfferAssignmentStatus,
    MobileOfferCatalogEntry,
    MobileOfferProvider,
} from '@numeo/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { mobileOfferError, mobileOffersApi } from './api'
import { findCustomerAssignment, matchesCustomerAssignment } from './customer-assignment-reconciliation'
import { isReviewedStoreOfferCurrent } from './assignmentStores'

type StoreResult =
    | { status: 'checking' }
    | { status: 'ready'; request: AssignMobileOfferRequest }
    | { status: 'assigned'; request: AssignMobileOfferRequest }
    | { status: 'error'; message: string }
type Results = Record<MobileOfferProvider, StoreResult>
const providers: MobileOfferProvider[] = ['apple', 'google']
const failureMessage = (failure: unknown) => (failure instanceof Error && failure.name === 'Error' ? failure.message : mobileOfferError(failure))

/** Both is orchestration only. Every write retains the normal single-store idempotency/eligibility checks. */
export function useBothStoreAssignment(offer: MobileOfferCatalogEntry, request: Omit<AssignMobileOfferRequest, 'provider'>, onHistoryRefresh: () => void) {
    const [results, setResults] = useState<Results>({ apple: { status: 'checking' }, google: { status: 'checking' } })
    const [busy, setBusy] = useState(false)
    const [consent, setConsent] = useState(false)
    const inFlight = useRef(false)
    const mounted = useRef(false)
    const checks = useRef({ apple: 0, google: 0 })

    const update = useCallback((provider: MobileOfferProvider, result: StoreResult) => {
        if (mounted.current) setResults((previous) => ({ ...previous, [provider]: result }))
    }, [])
    const validate = useCallback(
        async (candidate: AssignMobileOfferRequest) => {
            const current = await mobileOffersApi.find(offer.id)
            if (!isReviewedStoreOfferCurrent(current, offer, candidate.provider)) {
                throw new Error('Offer prices or configuration changed. Close this review and select the offer again.')
            }
            if (Date.parse(candidate.redeemBy) <= Date.now())
                throw new Error('The redemption deadline has passed. Close this review and choose a future deadline.')
            if (candidate.provider === 'apple') {
                const { offerId, userId, provider, redeemBy, expectedRevision } = candidate
                const readiness = await mobileOffersApi.assignmentReadiness({ offerId, userId, provider, redeemBy, expectedRevision })
                if (readiness.status !== MobileOfferAssignmentReadinessStatus.READY) throw new Error(readiness.message)
            }
        },
        [offer]
    )
    const prepare = useCallback(
        async (provider: MobileOfferProvider) => {
            const sequence = ++checks.current[provider]
            const isCurrent = () => mounted.current && sequence === checks.current[provider]
            update(provider, { status: 'checking' })
            try {
                let candidate: AssignMobileOfferRequest = { ...request, provider }
                const existing = await findCustomerAssignment(mobileOffersApi.history, request.userId, (record) => matchesCustomerAssignment(record, candidate))
                if (!isCurrent()) return
                if (existing) {
                    if (existing.status !== MobileOfferAssignmentStatus.ASSIGNED || Date.parse(existing.redeemBy) <= Date.now())
                        throw new Error(
                            `This store's previous assignment is ${existing.status}. Review its history using the single-store option; it will not be reopened automatically.`
                        )
                    // An uncertain prior POST may have saved a row. Retry only its canonical terms/revision.
                    candidate = { ...candidate, redeemBy: existing.redeemBy, reason: existing.reason, expectedRevision: existing.revision ?? 0 }
                }
                await validate(candidate)
                if (isCurrent()) update(provider, { status: 'ready', request: candidate })
            } catch (failure) {
                if (isCurrent()) update(provider, { status: 'error', message: failureMessage(failure) })
            }
        },
        [request, update, validate]
    )
    useEffect(() => {
        mounted.current = true
        void Promise.all(providers.map(prepare))
        return () => {
            mounted.current = false
        }
    }, [prepare])

    const confirm = async () => {
        if (!consent || inFlight.current) return
        const ready = providers.filter((provider) => results[provider].status === 'ready')
        if (!ready.length) return
        inFlight.current = true
        setBusy(true)
        setConsent(false)
        try {
            await Promise.all(
                ready.map(async (provider) => {
                    const state = results[provider]
                    if (state.status !== 'ready') return
                    let started = false
                    try {
                        await validate(state.request)
                        if (!mounted.current) return
                        started = true
                        await mobileOffersApi.assign(state.request)
                        update(provider, { status: 'assigned', request: state.request })
                    } catch (failure) {
                        update(provider, {
                            status: 'error',
                            message: `${failureMessage(failure)}${started ? ' Delivery is unconfirmed; an assignment may have been saved. Recheck this store before retrying.' : ''}`,
                        })
                    }
                })
            )
        } finally {
            inFlight.current = false
            if (mounted.current) {
                setBusy(false)
                onHistoryRefresh()
            }
        }
    }
    const retry = (provider: MobileOfferProvider) => {
        if (inFlight.current || results[provider].status !== 'error') return
        setConsent(false)
        void prepare(provider)
    }
    return { results, busy, consent, setConsent, confirm, retry }
}
